import { transform, isEqual, isObject } from 'lodash'
import moment from 'moment'

/**
 * https://gist.github.com/Yimiprod/7ee176597fef230d1451
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export const difference = (object: $TSFIXME, base: $TSFIXME) => {
  const changes = (object: $TSFIXME, base: $TSFIXME) => {
    return transform(object, function(result: $TSFIXME, value: $TSFIXME, key: string) {
      if (!isEqual(value, base[key])) {
        result[key] =
          isObject(value) && isObject(base[key])
            ? changes(value, base[key])
            : value
      }
    })
  }
  return changes(object, base)
}

/**
 * Status enum for data operations
 */
export const STATUS = {
  IDLE: 'IDLE',
  PENDING: 'PENDING',
  RESOLVED: 'RESOLVED',
  REJECTED: 'REJECTED',
} as const

export type StatusKey = keyof typeof STATUS
export type StatusValue = typeof STATUS[StatusKey]

export const isLoading = (status: string): boolean =>
  status === STATUS.IDLE || status === STATUS.PENDING

// https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
export const componentToHex = (c: $TSFIXME) => {
  const hex = c.toString(16)
  return hex.length === 1 ? '0' + hex : hex
}

export const rgbToHex = (r: $TSFIXME, g: $TSFIXME, b: $TSFIXME) => {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

// This fn has been modified to accept a fallback and handle 4 letter hex specifications e.g. #FFF
export const hexToRgb = (hex: string, fallback: string) => {
  if (!hex) {
    return fallback ? fallback : { r: 255, g: 255, b: 255 }
  }
  let normalizedHex = hex
  if (hex && hex.length === 4) {
    const hexOnly = hex.substring(1, 4)
    const r = hexOnly[0],
      g = hexOnly[1],
      b = hexOnly[2]
    normalizedHex = `#${r}${r}${g}${g}${b}${b}`
  }
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalizedHex)
  return result
    ? {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
      r: parseInt(result[1] as string, 16),
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
      g: parseInt(result[2] as string, 16),
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
      b: parseInt(result[3] as string, 16),
    }
    : null
}

// Alpha can be a string or a decimal
// Hex should be a string beginning with a #
// fallback should be a hex value
export const hexToRgbaStyle = (hex: string, alpha: string, fallback: string): string => {
  const { r, g, b }: $TSFIXME = hexToRgb(hex, fallback)
  const rgbaString = `rgba(${r},${g},${b},${alpha})`
  return rgbaString
}

/**
 * CustomError to add instructions with an Error
 * @param  string message - error message
 * @param  string instructions - instructions to follow to resolve the error
 * @return {Error} Return a new custom error
 */
export interface ErrorWithInstructionsProps extends Error {
  instructions?: string,
}
export const ErrorWithInstructions = (message: string, instructions: string) => {
  const error: ErrorWithInstructionsProps = new Error(message)
  error.instructions = instructions
  return error
}
ErrorWithInstructions.prototype = Object.create(Error.prototype)

// return a humanized string if duration is greater than or equal to 24 hours
export const formatDuration = (duration: number): string => {
  if (duration >= 86400000) {
    return moment.duration(duration).humanize()
  } else {
    return moment.utc(duration).format('HH:mm:ss')
  }
}
